import type { ComputedRef, Ref } from 'vue';
import {
  cloneDeep,
  concat,
  difference,
  get,
  set,
  without,
} from 'lodash-es';
import {
  computed,

  ref,

  toRaw,
} from 'vue';

import type { ITreeItem } from '@components/tree/VTree/types';
import type { IRolesNamespaces } from '@modules/roles-and-permissions/types';
import { useSaveRolePermissionsMutation } from '@modules/roles-and-permissions/mutations';
import { useRolePermissionsQuery, useRolesAndTreeQuery } from '@modules/roles-and-permissions/queries';
import { assert, isNumber, isString } from '@utils/guards';
import { watchImmediate } from '@vueuse/core';

import type { IPermissionsTreeMap } from '../types';
import { buildTreeItems } from '../utils';

export interface PermissionsTreeComposable {
  items: ComputedRef<ITreeItem[]>;
  rolesPermissionsMap: Ref<IPermissionsTreeMap>;
  isRolesAndTreeLoading: Ref<boolean>;
  isRolePermissionsLoading: Ref<boolean>;
  isRolePermissionsFetching: Ref<boolean>;
  isSavePermissionsLoading: Ref<boolean>;
  handleChangePermission(node: ITreeItem, isEnabled?: boolean): void;
  isPermissionEnabled(node: ITreeItem): boolean | undefined;
  isPermissionsChanged(node: ITreeItem): boolean;
  handleSave(node: ITreeItem): void;
  handleReset(node: ITreeItem): void;
}

export default function usePermissionsTree(
  namespace: Ref<IRolesNamespaces>,
  roleId: Ref<number | undefined>,
): PermissionsTreeComposable {
  const rolesPermissionsMap = ref<IPermissionsTreeMap>({});

  const { data: rolesAndTreeData, isLoading: isRolesAndTreeLoading } = useRolesAndTreeQuery(namespace);

  const {
    data: rolePermissionsTree,
    isLoading: isRolePermissionsLoading,
    isFetching: isRolePermissionsFetching,
  } = useRolePermissionsQuery(roleId);

  const { mutate: savePermissions, isLoading: isSavePermissionsLoading } = useSaveRolePermissionsMutation(roleId);

  const items = computed<ITreeItem[]>(() => buildTreeItems(rolesAndTreeData.value?.permissions_tree));

  watchImmediate(rolePermissionsTree, (newValue) => {
    if (roleId.value && newValue && !rolesPermissionsMap.value[roleId.value]) {
      rolesPermissionsMap.value[roleId.value] = cloneDeep(newValue);
    }
  });

  function getGroupPermissions(groupName: string): string[] | undefined {
    assert(isNumber(roleId.value));

    return get(rolesPermissionsMap.value, `${roleId.value}.${groupName}`);
  }

  function setGroupPermissions(groupName: string, newValue: string[]): void {
    assert(isNumber(roleId.value));

    set(rolesPermissionsMap.value, `${roleId.value}.${groupName}`, newValue);
  }

  function includePermission(groupName: string, permission: string): void {
    setGroupPermissions(groupName, concat(getGroupPermissions(groupName) ?? [], permission));
  }

  function excludePermission(groupName: string, permission: string): void {
    setGroupPermissions(groupName, without(getGroupPermissions(groupName) ?? [], permission));
  }

  function handleChangePermission(node: ITreeItem, isEnabled?: boolean): void {
    assert(isString(node.key));

    if (isEnabled) {
      includePermission(node.path, node.key);
    } else {
      excludePermission(node.path, node.key);
    }
  }

  function isPermissionEnabled(node: ITreeItem): boolean | undefined {
    assert(isString(node.key));

    return getGroupPermissions(node.path)?.includes(node.key);
  }

  function isPermissionsChanged(node: ITreeItem): boolean {
    const nodePermissions = toRaw(getGroupPermissions(node.path));
    const basePermissions = toRaw(rolePermissionsTree.value?.[node.path]);

    if (!rolePermissionsTree.value || !nodePermissions)
      return false;

    const diffWithBase = difference(basePermissions, nodePermissions);
    const diffWithCurrent = difference(nodePermissions, basePermissions ?? []);

    return !!diffWithBase?.length || !!diffWithCurrent.length;
  }

  function handleSave(node: ITreeItem): void {
    savePermissions({ actions: getGroupPermissions(node.path) ?? [], controller_name: node.path });
  }

  function handleReset(node: ITreeItem): void {
    setGroupPermissions(node.path, [...(rolePermissionsTree.value?.[node.path] ?? [])]);
  }

  return {
    items,
    rolesPermissionsMap,
    isRolesAndTreeLoading,
    isRolePermissionsLoading,
    isRolePermissionsFetching,
    isSavePermissionsLoading,
    handleChangePermission,
    isPermissionEnabled,
    isPermissionsChanged,
    handleSave,
    handleReset,
  };
}
